import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { STAFF_NAV } from "@/lib/staff-nav";
import { OperationsBanner } from "@/components/staff/OperationsBanner";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface Task {
  id: string;
  title: string;
  status: string;
  due_date: string | null;
}

export const Route = createFileRoute("/staff/tasks")({
  head: () => ({
    meta: [
      { title: "Tasks — Operations" },
      { name: "description", content: "Create, update, and close operational tasks." },
      { property: "og:title", content: "Tasks — Operations" },
      { property: "og:description", content: "Create, update, and close operational tasks." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StaffTasks,
});

function StaffTasks() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Task[]>([]);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");

  const load = async () => {
    const { data } = await supabase
      .from("tasks")
      .select("id, title, status, due_date")
      .order("created_at", { ascending: false });
    setRows((data as Task[]) ?? []);
  };
  useEffect(() => { void load(); }, []);

  const create = async () => {
    if (!title.trim() || !user) return;
    const { error } = await supabase.from("tasks").insert({
      assignee_id: user.id,
      title: title.trim(),
      due_date: due || null,
      status: "open",
    });
    if (error) { toast.error(error.message); return; }
    setTitle(""); setDue("");
    toast.success("Task created");
    void load();
  };

  const close = async (id: string) => {
    const { error } = await supabase.from("tasks").update({ status: "done" }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    void load();
  };

  return (
    <ProtectedRoute requiredRole="staff">
      <PortalShell title="Tasks" nav={STAFF_NAV}>
        <OperationsBanner />
        <Eyebrow>Workflow</Eyebrow>
        <h1 className="mt-3 font-serif text-3xl font-semibold text-navy-900">Tasks</h1>

        <div className="mt-6 flex flex-col gap-3 rounded-sm border border-bronze-600/30 bg-white p-4 sm:flex-row sm:items-end">
          <label className="flex-1 font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
            Title
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block min-h-11 w-full rounded-sm border border-bronze-600/40 bg-cream-50 px-3 text-base text-charcoal-900"
            />
          </label>
          <label className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
            Due
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className="mt-1 block min-h-11 w-full rounded-sm border border-bronze-600/40 bg-cream-50 px-3 text-base text-charcoal-900"
            />
          </label>
          <PrimaryButton onClick={() => void create()}>Add task</PrimaryButton>
        </div>

        <ul className="mt-6 divide-y divide-bronze-600/15 rounded-sm border border-bronze-600/30 bg-white">
          {rows.length === 0 && <li className="px-4 py-6 font-sans text-sm text-charcoal-500">No tasks.</li>}
          {rows.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div>
                <p className="font-sans text-sm text-charcoal-900">{t.title}</p>
                <p className="font-sans text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
                  {t.status}{t.due_date ? ` · due ${t.due_date}` : ""}
                </p>
              </div>
              {t.status !== "done" && (
                <button
                  onClick={() => void close(t.id)}
                  className="min-h-11 rounded-sm border border-bronze-600 px-4 font-sans text-[12px] font-semibold uppercase tracking-[0.08em] text-navy-800"
                >
                  Close
                </button>
              )}
            </li>
          ))}
        </ul>
      </PortalShell>
    </ProtectedRoute>
  );
}
